/**
 * The numbers this screen's layout is computed from, shown on the device that has the bug.
 *
 * **Three fixes for the iOS keyboard have shipped and all three were wrong**, each reasoned
 * carefully from a belief about what iOS does to the viewport when the keyboard opens. The
 * beliefs were the problem: `innerHeight` shrinks, or it does not; `offsetTop` moves, or it
 * does not; `dvh` tracks the keyboard, or it does not. Every one of those is answerable in a
 * second on the actual handset and unanswerable from here, and guessing has now cost the
 * owner three rounds of testing something I could not see.
 *
 * So this exists to end the guessing rather than to be a feature. It is behind `?diag=1`,
 * renders nothing otherwise, and is not linked from anywhere.
 *
 * It deliberately reports **raw readings, not conclusions**. If it said "keyboard: 291px" it
 * would be asserting the same arithmetic that has been wrong three times; what is wanted is
 * the inputs, so the arithmetic can be checked against them instead of believed.
 */
import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { setDebug } from '../debug';

interface Reading {
  innerHeight: number;
  visualHeight: number;
  visualOffsetTop: number;
  visualScale: number;
  rectTop: number;
  rectBottom: number;
  appliedHeight: string;
  scrollTop: number;
  dvh: number;
  standalone: boolean;
  focused: string;
}

/** Whether the diagnostic was asked for. Read once: it cannot change without a navigation. */
export function diagnosticsRequested(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('diag') === '1';
  } catch {
    return false;
  }
}

export default function Diagnostics({ root }: { root: RefObject<HTMLElement | null> }) {
  const [reading, setReading] = useState<Reading>();
  /*
   * Local as well as stored. `debugOn()` is read at render time by the parent, so clearing
   * the store alone leaves this mounted until something else re-renders `Chat` — which, on a
   * screen whose whole job is not to redraw while somebody reads, may be several seconds.
   */
  const [off, setOff] = useState(false);

  useEffect(() => {
    let frame = 0;

    const read = () => {
      frame = 0;
      const element = root.current;
      const view = window.visualViewport;
      const rect = element?.getBoundingClientRect();
      /*
       * `100dvh` resolved by the browser rather than assumed. This is the one that decides
       * whether CSS can see the keyboard at all, and it is the belief the first two fixes
       * rested on.
       */
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;height:100dvh;width:0;visibility:hidden';
      document.body.appendChild(probe);
      const dvh = probe.getBoundingClientRect().height;
      probe.remove();

      setReading({
        innerHeight: Math.round(window.innerHeight),
        visualHeight: view ? Math.round(view.height) : -1,
        visualOffsetTop: view ? Math.round(view.offsetTop) : -1,
        visualScale: view ? Number(view.scale.toFixed(2)) : -1,
        rectTop: rect ? Math.round(rect.top) : -1,
        rectBottom: rect ? Math.round(rect.bottom) : -1,
        appliedHeight: element?.style.getPropertyValue('--chat-view-h') || '(unset)',
        scrollTop: Math.round(document.scrollingElement?.scrollTop ?? -1),
        dvh: Math.round(dvh),
        standalone:
          window.matchMedia('(display-mode: standalone)').matches ||
          (navigator as { standalone?: boolean }).standalone === true,
        focused: document.activeElement?.tagName.toLowerCase() ?? '-',
      });
    };

    const schedule = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(read);
    };

    read();
    const view = window.visualViewport;
    view?.addEventListener('resize', schedule);
    view?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    // Focus and blur, because the keyboard opening is the moment being measured and it does
    // not always end in a `resize` this page hears.
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    const timer = window.setInterval(read, 500);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.clearInterval(timer);
      view?.removeEventListener('resize', schedule);
      view?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
    };
  }, [root]);

  if (off || !reading) return null;

  /*
   * Pinned to the *visual* viewport by `position: fixed`, which on iOS follows the visible
   * window. If this panel itself scrolls out of view while the keyboard is up, that is a
   * reading in its own right and worth reporting.
   *
   * **Tapping it turns it off, and that is not a convenience.** Leo, with the panel up:
   * *"Now it works but I can't hide debug it covers nabu."* It sat at `top: 0` over the
   * wordmark — and holding the wordmark is the only gesture that toggles it, so the overlay
   * was covering its own off switch. A debug tool that cannot be dismissed from the device
   * it is debugging is a bug the same way the keyboard was.
   *
   * Two changes rather than one, because either alone leaves a hole. It now clears the bar
   * (`top: var(--bar-h)` in the stylesheet), so the wordmark and the menu stay reachable
   * while it is up; and it is a button, so the panel itself is the off switch and nothing
   * has to be aimed at underneath it. `setDebug(false)` rather than local state alone, so it
   * stays off across a navigation and a relaunch — the same store the hold gesture writes.
   */
  return (
    <button
      type="button"
      className="chat-diag"
      onClick={() => {
        setDebug(false);
        setOff(true);
      }}
      aria-label="Hide diagnostics"
    >
      {[
        `standalone   ${reading.standalone}`,
        `innerHeight  ${reading.innerHeight}`,
        `visual.h     ${reading.visualHeight}`,
        `visual.top   ${reading.visualOffsetTop}`,
        `visual.scale ${reading.visualScale}`,
        `100dvh       ${reading.dvh}`,
        `rect.top     ${reading.rectTop}`,
        `rect.bottom  ${reading.rectBottom}`,
        `--chat-view-h ${reading.appliedHeight}`,
        `scrollTop    ${reading.scrollTop}`,
        `focus        ${reading.focused}`,
        '— tap to hide —',
      ].join('\n')}
    </button>
  );
}
